<script lang="ts" setup>
import { ButtonStyle } from '~/static/enums/components/button'
import AButton from '~/components/atoms/buttons/Button.vue'
import AIconLogo from '~/components/atoms/icons/Logo.vue'
import RModalSupport from '~/components/organs/modals/Support.vue'
import RModalInstruction from '~/components/organs/modals/Instruction.vue'
import RModalSignIn from '~/components/organs/modals/SignIn.vue'
import MDropdownMenu from '~/components/molecules/DropdownMenu.vue'
import RModalNameChange from '~/components/organs/modals/NameChange.vue'
import RModalDeleteFile from '~/components/organs/modals/DeleteFile.vue'
import RModalDownloadNotAvailable from '~/components/organs/modals/DownloadNotAvailable.vue'
import MAlert from '~/components/molecules/Alert.vue'
import RModalContainer from '~/components/organs/ModalContainer.vue'
import { CardType } from '~/static/enums/card-type'
import { useEditorStore } from '~/store/editor'
import { storeToRefs } from 'pinia'
import { useAuth } from '~/store/auth'
import { openModal, closeModal } from '~/utils/Modal'
import { Dropdown as FDropdown, VTooltip as vTooltip } from 'floating-vue'
import { PopupType } from '~/static/enums/popup'
import RModalMobileNav from '~/components/organs/modals/MobileNav.vue'
import { useRemovalBackgroundStore } from '~/store/removal-background'
import { BackgroundRemovalStatus } from '~/static/enums/image/background-removal-status'
import { useToolsStore } from '~/store/tools/tools'
import { SupportType } from '~/static/enums/support-type'

interface Props {
  isEditor: boolean
}

withDefaults(defineProps<Props>(), {
  isEditor: false,
})

const backgroundRemovalStore = useRemovalBackgroundStore()
const { backgroundRemovalStatus, imageUrl } = storeToRefs(backgroundRemovalStore)

const isInfograficState = computed(() => {
  return backgroundRemovalStatus.value === BackgroundRemovalStatus.Pending && imageUrl.value !== null
})

const isTemplate = computed<boolean>(() => {
  return useRoute().name === 'template-id'
})

const authStore = useAuth()
const { isAuth, isAdmin, user } = storeToRefs(authStore)

const { $listen, $off, $event } = useNuxtApp()

const isPaid = computed(() => {
  return user.value && user.value.subscription
})

const store = useEditorStore()
const {
  id,
  name,
  nameForChanging,
  nameError,
  type,
} = storeToRefs(store)

const popupType = ref<PopupType | false>(false)
const popupText = ref('')

onMounted(() => {
  window.addEventListener('storage', storageListener)
  $listen('editor:saved', cardSaved)
  $listen('editor:save-error', cardSavingError)
  $listen('popup:show', showPopup)
  $listen('editor:chage-size', changeSize)
})
onBeforeUnmount(() => {
  window.removeEventListener('storage', storageListener)
  $off('editor:saved', cardSaved)
  $off('editor:save-error', cardSavingError)
  $off('popup:show', showPopup)
  $off('editor:chage-size', changeSize)
})

const changeSize = (newSize: {
  width: number
  height: number
}) => {
  size.value = newSize
}

const storageListener = (event: StorageEvent) => {
  if (event.key === 'reset-user') {
    authStore.getUser()
  }
}

const showPopup = (payload: { type?: PopupType, text: string, closeAfter?: number }) => {
  popupType.value = payload.type ?? PopupType.Warning
  popupText.value = payload.text
  if (payload.closeAfter) {
    setTimeout(closePopup, payload.closeAfter * 1000)
  }
}

const closePopup = () => {
  popupType.value = false
}

const saved = ref(false)
const cardSaved = () => {
  stopSavingProcess()
  saved.value = true
  setTimeout(() => {
    saved.value = false
  }, 3000)
}
const cardSavingError = () => {
  stopSavingProcess()
  $event('popup:show', { text: 'Произошла ошибка, обратитесь в службу поддержки' })
}

const savingProcess = ref(false)
const saveActualData = () => {
  savingProcess.value = true
  $event('editor:save')
}
const stopSavingProcess = () => {
  savingProcess.value = false
}

const deleteFile = () => {
  store.delete()
}

const openChangeNameModal = async () => {
  const modal = await openModal(RModalNameChange, {
    name: nameForChanging,
    error: nameError,
  })
  modal.on('change-name', changeNameForChanging)
  modal.on('save', changeName)
  modal.on('cancel', cancelChangingName)
}
const changeNameForChanging = (value: string) => {
  nameForChanging.value = value
}
const changeName = async () => {
  if (await store.changeName()) {
    closeModal()
  }
}
const cancelChangingName = () => {
  nameForChanging.value = name.value
  nameError.value = ''
}

const signinModalTitle = ref('Вход')

const openSigninModal = (title: string): void => {
  signinModalTitle.value = title
  openModal(RModalSignIn, { title: signinModalTitle })
}

const userMenu = computed(() => {
  return [
    { url: '#', text: user.value?.email ?? '', disabled: true },
    { url: '#', text: 'Выйти', handler: logout },
  ]
})

const menu = computed(() => {
  const items = []
  if (isAdmin.value && type.value === CardType.ProductCard) {
    items.push({ url: '#', text: 'Добавить в шаблоны', handler: addToTemplates })
  }
  if (isAdmin.value && type.value === CardType.Template) {
    items.push({ url: '#', text: 'Убрать из шаблонов', handler: removeFromTemplates })
  }
  items.push(
    { url: '#', text: 'Переименовать', handler: openChangeNameModal, testid: 'header-change-name' },
  )

  if (isAuth.value) {
    items.push({ url: '#', text: 'Сделать копию', handler: cloneCard, testid: 'header-clone-card' })
  }
  if (id.value !== null) {
    items.push({ url: '#', text: 'Удалить', handler: openDeleteFileModal, testid: 'header-delete-card' })
  }
  return items
})

const size = ref({
  width: 900,
  height: 1200,
})

const downloadMenu = computed(() => {
  return [
    {
      url: '#',
      text: 'Текущий размер',
      size: `${size.value.width} x ${size.value.height}`,
      handler: () => {
        downloadHandler(size.value)
      },
      testid: 'current-size',
    },
    {
      url: '#',
      text: 'Увеличенный x2',
      size: `${size.value.width * 2} x ${size.value.height * 2} ⋅ Лучшее качество`,
      handler: () => {
        downloadHandler({
          width: size.value.width * 2,
          height: size.value.height * 2,
        })
      },
      testid: 'improved-size',
    },
  ]
})

const addToTemplates = () => {
  if (id.value === null) {
    const add = () => {
      store.addToTemplates()
      $off('editor:saved', add)
    }
    $event('editor:save')
    $listen('editor:saved', add)
  } else {
    store.addToTemplates()
  }
}

const removeFromTemplates = () => {
  store.removeFromTemplates()
}

const cloneCard = () => {
  $event('editor:clone')
}

const logout = (): void => {
  authStore.logout()
}

const toCards = (): void => {
  if (isTemplate.value) {
    useRouter().push('/templates')
    return
  }
  if (isAuth.value) {
    useRouter().push('/')
  } else {
    closeModal()
    nextTick(() => {
      openSigninModal('Вход')
    })
  }
}

const openDeleteFileModal = async () => {
  const modal = await openModal(RModalDeleteFile, {
    name: name,
  })
  modal.on('delete', deleteFile)
}

const downloadingProcess = ref(false)
const markDownload = (size: {
  width: number
  height: number
}) => {
  track('Image downloaded')
  authStore.markDownload().then((result) => {
    if (result) {
      $event('editor:download', size)
      downloadingProcess.value = true
      $listen('editor:downloaded', cardDownloaded)
    }
  })
}

const cardDownloaded = () => {
  downloadingProcess.value = false
  $off('editor:downloaded')
}

const openMobileEditorNav = () => {
  const items: {
    callback: () => void
    text: string
    showOnTablet?: boolean
  }[] = []

  if (isAdmin.value && type.value === CardType.ProductCard) {
    items.push({ text: 'Добавить в шаблоны', callback: addToTemplates })
  }
  if (isAdmin.value && type.value === CardType.Template) {
    items.push({ text: 'Убрать из шаблонов', callback: removeFromTemplates })
  }

  items.push({ callback: openChangeNameModal, text: 'Переименовать' })

  if (isAuth.value) {
    items.push({ callback: cloneCard, text: 'Сделать копию' })
  }

  if (store.id !== null) {
    items.push({ callback: openDeleteFileModal, text: 'Удалить' })
  }

  items.push(
    {
      callback: () => {
        openModal(RModalSupport, { type: SupportType.Telegram })
      }, text: 'Поддержка', showOnTablet: false,
    },
    {
      callback: () => {
        openModal(RModalInstruction)
      }, text: 'Инструкция', showOnTablet: false,
    },
    { callback: toCards, text: 'Все карточки', showOnTablet: false },
  )
  openModal(RModalMobileNav, {
    title: name,
    items: items,
  })
}

const openMobileHomeNav = () => {
  openModal(RModalMobileNav, {
    title: 'Ещё',
    items: [
      {
        callback: () => {
          openModal(RModalSupport, { type: SupportType.Telegram })
        }, text: 'Поддержка',
      },
      {
        callback: () => {
          openModal(RModalInstruction)
        }, text: 'Инструкция',
      },
      {
        callback: () => {
          closeModal()
          useRouter().push('/pricing')
        }, text: 'Тарифы',
      },
      {
        callback: () => {
          closeModal()
          useRouter().push('/tools')
        }, text: 'Инструменты',
      },
    ],
  })
}

const downloadHandler = (size: {
  width: number
  height: number
}) => {
  if (!isAuth.value) {
    openSigninModal('Войдите, чтобы скачать')
  } else if (downloadingProcess.value) {
    return
  } else if (isPaid.value || user.value?.remaining_downloads !== 0) {
    markDownload(size)
  } else {
    openModal(RModalDownloadNotAvailable)
  }
}

const downloadOnClient = () => {
  $event('editor:download', {
    width: size.value.width,
    height: size.value.height,
    side: 'client',
  })
}

const downloadButtonTitle = computed(() => {
  if (!isAuth.value) {
    return 'Скачать 3/3'
  } else if (isPaid.value) {
    return 'Скачать'
  } else {
    return 'Скачать ' + (user.value?.remaining_downloads ?? 3) + '/3'
  }
})

const text = '<span class="p-4-8">Сохранено <a href="/" class="tooltip-link">в профиле</a><span>'

const toolsStore = useToolsStore()
const { menuItems } = storeToRefs(toolsStore)

</script>

<template>
  <div
    :class="{ 'editor': isEditor }"
    class="header"
  >
    <div class="container-lg">
      <div class="d-flex align-items-center position-relative">
        <div class="header__left">
          <NuxtLink
            :to="isAuth ? '/files' : '/'"
            :class="{ 'd-none d-md-block': isEditor }"
            aria-label="Magvi"
          >
            <AIconLogo
              class="logo me-3"
              :class="{ 'text-light': isEditor }"
            />
          </NuxtLink>
          <ul class="nav">
            <li
              v-if="!isEditor"
              class="nav-item"
            >
              <NuxtLink
                to="/templates"
                class="nav-link px-12"
              >
                Шаблоны
              </NuxtLink>
            </li>
            <li class="nav-item hidden-md">
              <a
                :class="{ 'nav-link--editor': isEditor }"
                class="nav-link px-12"
                href="#"
                @click.prevent="openModal(RModalSupport, { type: SupportType.Telegram })"
              >Поддержка</a>
            </li>
            <li class="nav-item hidden-md">
              <a
                :class="{ 'nav-link--editor': isEditor }"
                class="nav-link px-12"
                href="#"
                @click.prevent="openModal(RModalInstruction)"
              >Инструкция</a>
            </li>
            <li
              v-if="!isEditor"
              class="nav-item hidden-lg"
            >
              <NuxtLink
                to="/pricing"
                :class="{ 'nav-link--editor': isEditor }"
                class="nav-link px-12"
              >
                Тарифы
              </NuxtLink>
            </li>
            <li
              v-if="!isEditor"
              class="nav-item hidden-lg"
            >
              <FDropdown
                :distance="16"
                :delay="0.3"
                popper-class="v-popper-tools"
              >
                <NuxtLink
                  to.prevent="#"
                  class="nav-link px-12 current-item"
                >
                  <span>Инструменты</span>
                  <span class="ms-1">
                    <BootstrapIconChevronDown class="icon-size-16" />
                  </span>
                </NuxtLink>
                <template #popper="{ hide }">
                  <ul
                    class="submenu"
                    @click="hide"
                  >
                    <li>
                      <BootstrapIconPaintBucket class="icon-size-22" />
                    </li>
                    <li>
                      <NuxtLink to="/tools">
                        <b>Изменение цвета фона</b>
                      </NuxtLink>
                    </li>
                    <li
                      v-for="item in menuItems"
                      :key="item.slug"
                    >
                      <NuxtLink :to="'/tools/' + item.slug">
                        {{ item.nameInMenu }}
                      </NuxtLink>
                    </li>
                    <li>
                      <NuxtLink to="/tools">
                        Другие цвета
                      </NuxtLink>
                    </li>
                  </ul>
                </template>
              </FDropdown>
            </li>
            <li
              v-if="!isEditor"
              class="nav-item d-lg-none"
            >
              <div
                class="nav-link px-12"
                @click="openMobileHomeNav"
              >
                <span>Ещё</span>
                <span class="ms-1">
                  <BootstrapIconCaretDownFill class="icon-size-10" />
                </span>
              </div>
            </li>
            <li
              v-if="isEditor"
              class="nav-item"
            >
              <button
                class="btn-logo d-md-none"
                @click="openMobileEditorNav"
              >
                <AIconLogo
                  class="logo me-1 text-light"
                />
                <BootstrapIconChevronDown
                  class="text-light icon-size-20"
                />
              </button>
              <div
                class="nav-link px-12 d-none d-md-block d-lg-none"
                :class="{ 'nav-link--editor': isEditor }"
                @click="openMobileEditorNav"
              >
                <span>Ещё</span>
                <span class="ms-1">
                  <BootstrapIconCaretDownFill class="icon-size-10" />
                </span>
              </div>
            </li>
          </ul>
        </div>

        <div
          v-if="isEditor"
          class="header__center hidden-lg"
        >
          <template v-if="isTemplate">
            <div class="dropdown d-flex">
              <span
                class="header__center_right dropdown-toggle d-flex"
                type="button"
                ref="cardDropDown"
              >
                <span
                  class="header__center_title"
                  data-testid="header-center-title"
                >{{ name }}</span>
              </span>
            </div>
          </template>
          <template v-else>
            <div class="d-flex">
              <span
                class="header__center_left"
                @click="toCards"
              >
                Карточки
              </span>
              <span class="header__center_slash">&nbsp;/&nbsp;</span>
            </div>
            <div class="dropdown d-flex">
              <FDropdown
                :distance="21"
                :delay="0.3"
              >
                <span
                  class="header__center_right hover dropdown-toggle d-flex"
                  type="button"
                  ref="cardDropDown"
                >
                  <span
                    class="header__center_title"
                    data-testid="header-center-title"
                  >{{ name }}</span>
                  <span class="ms-1">
                    <BootstrapIconCaretDownFill class="icon-size-10" />
                  </span>
                </span>
                <template #popper="{ hide }">
                  <MDropdownMenu
                    :items="menu"
                    :custom-arrow="true"
                    theme="dark"
                    @click="hide"
                  />
                </template>
              </FDropdown>
            </div>
          </template>
        </div>

        <div class="header__right">
          <div
            v-if="!isEditor"
            class="d-flex align-items-center"
          >
            <iframe
              v-if="!isPaid"
              src="https://yandex.ru/sprav/widget/rating-badge/176658118910?type=rating"
              width="150"
              height="50"
              frameborder="0"
              class="d-none d-md-block mr-12"
              title="yandex rate"
            />
            <div v-if="!isAuth">
              <AButton
                :button-style="ButtonStyle.OutlinePrimary"
                class="d-none d-md-block"
                @click="openSigninModal('Вход')"
              >
                Войти
              </AButton>
              <button
                class="d-md-none btn-login color-primary"
                @click="openSigninModal('Вход')"
                aria-label="Вход"
              >
                <BootstrapIconBoxArrowInRight class="icon-size-24" />
              </button>
            </div>
            <div
              v-else
              class="dropdown"
            >
              <FDropdown
                placement="bottom-end"
                :distance="10"
                :delay="0.3"
              >
                <button
                  class="btn-avatar dropdown-toggle"
                  type="button"
                  ref="userDropDown"
                >
                  <BootstrapIconPerson class="icon-size-20" />
                </button>
                <template #popper="{ hide }">
                  <MDropdownMenu
                    :items="userMenu"
                    position="end"
                    @click="hide"
                  />
                </template>
              </FDropdown>
            </div>
          </div>

          <div v-else>
            <div class="d-flex">
              <AButton
                v-if="isAdmin"
                :button-style="ButtonStyle.OutlineLight"
                :disabled="isInfograficState"
                :style="downloadingProcess ? 'position: relative; pointer-events: none;' : ''"
                class="me-2 prevent_unselection"
                type="button"
                data-testid="download"
                @click="downloadOnClient"
              >
                <div
                  v-if="downloadingProcess"
                  class="loading-area"
                >
                  <div
                    class="spinner-border"
                    role="status"
                  />
                </div>
                <span :style="downloadingProcess ? 'opacity: 0;' : ''">
                  Скачать на клиенте
                </span>
              </AButton>
              <FDropdown
                :distance="9"
                :delay="0.3"
              >
                <AButton
                  :button-style="ButtonStyle.OutlineLight"
                  :disabled="isInfograficState"
                  :style="downloadingProcess ? 'position: relative; pointer-events: none;' : ''"
                  class="me-2 prevent_unselection"
                  type="button"
                  data-testid="download"
                >
                  <div
                    v-if="downloadingProcess"
                    class="loading-area"
                  >
                    <div
                      class="spinner-border"
                      role="status"
                    />
                  </div>
                  <span :style="downloadingProcess ? 'opacity: 0;' : ''">
                    {{ downloadButtonTitle }}
                  </span>
                </AButton>
                <template #popper="{ hide }">
                  <MDropdownMenu
                    :items="downloadMenu"
                    :custom-arrow="true"
                    theme="dark"
                    class="download-menu"
                    @click="hide"
                  >
                    <template #additional="{ item }">
                      <div class="download-menu-size">
                        {{ item.size }}
                      </div>
                    </template>
                  </MDropdownMenu>
                </template>
              </FDropdown>
              <AButton
                v-if="!isAuth"
                :button-style="ButtonStyle.Primary"
                :disabled="isInfograficState"
                class="save-button prevent_unselection"
                @click="openSigninModal('Войдите, чтобы сохранить')"
              >
                Сохранить
              </AButton>
              <AButton
                v-else-if="savingProcess"
                :button-style="ButtonStyle.Primary"
                class="save-button prevent_unselection"
                style="position: relative; pointer-events: none;"
                @click.prevent
              >
                <div class="loading-area">
                  <div
                    class="spinner-border"
                    role="status"
                  />
                </div>
                <span style="opacity: 0;">
                  Сохранить
                </span>
              </AButton>
              <AButton
                v-else
                :button-style="ButtonStyle.Primary"
                :disabled="isInfograficState"
                class="save-button prevent_unselection"
                @click="saveActualData"
                v-tooltip="{
                  content: text,
                  html: true,
                  shown: saved,
                  triggers: [],
                  distance: 24,
                }"
              >
                <template v-if="!saved">
                  Сохранить
                </template>
                <BootstrapIconCheckCircle
                  v-else
                  class="icon-check"
                />
              </AButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="popupType"
    class="alert-wrap d-flex justify-content-center prevent_unselection"
  >
    <MAlert
      class="justify-content-between w-fit-content"
      :type="popupType"
      @close="closePopup"
    >
      <template #text>
        <div class="me-2">
          {{ popupText }}
        </div>
      </template>
    </MAlert>
  </div>
  <RModalContainer />

  <!-- TODO: Настроить вывод модального окна в адаптиве при клике на кнопку Скачать -->
  <!--<RModalMobileNav
    title="Размер для скачивания"
    :items="downloadMenu"
  >
    <template #additional="{ item }">
      <div class="mobile-download-menu-size">
        {{ item.size }}
      </div>
    </template>
  </RModalMobileNav>-->
</template>

<style lang="scss" scoped>
.loading-area {
  position: absolute;
  width: calc(100% - 24px);
  text-align: center;

  @include media-breakpoint-down(md) {
    width: calc(100% - 16px);
  }

  .spinner-border {
    width: rem(20px);
    height: rem(20px);
    border-width: 3px;
  }
}

.download-menu {
  :deep(.dropdown-item) {
    color: $white;
  }

  .download-menu-size {
    color: $gray-500;
  }
}

.mobile-download-menu-size {
  color: $gray-600;
}

.submenu {
  padding: rem(14px) rem(24px) rem(24px);
  margin: 0;
  list-style: none;

  li {
    &:not(:last-child) {
      margin-bottom: rem(4px);
    }
  }

  a {
    color: $gray-900;

    @media (any-hover: hover) {
      &:hover {
        color: $indigo-500;
      }
    }
  }
}

.v-popper--shown .current-item {
  font-weight: 700;

  :deep(svg) {
    path {
      stroke: $gray-900;
    }
  }
}
</style>

<style lang="scss">
.v-popper-tools {
  .v-popper__arrow-container {
    display: none;
  }
}
</style>
